import { Link } from "react-router-dom";
import { Heart, BedDouble, Bath, Maximize, MapPin } from "lucide-react";
import { priceLabel, statusColor } from "@/lib/format";
import { useSaved } from "@/context/SavedContext";

export default function PropertyCard({ property, className = "" }) {
  const { isSaved, toggleSave } = useSaved();
  const saved = isSaved(property.id);
  const image = property.image || property.images?.[0]?.url;
  const location = [property.area, property.town].filter(Boolean).join(", ");

  return (
    <div
      className={`group bg-white rounded-2xl border border-border overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-1 transition-[transform,box-shadow] duration-300 flex flex-col ${className}`}
      data-testid={`property-card-${property.id}`}
    >
      <Link to={`/property/${property.slug}`} className="relative block aspect-[4/3] overflow-hidden bg-muted">
        {image ? (
          <img
            src={image}
            alt={property.title || "Property"}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground text-sm">No image</div>
        )}
        <div className="absolute top-3 left-3 flex gap-2">
          {property.status && (
            <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide ${statusColor(property.status)}`}>
              {property.status}
            </span>
          )}
          {property.is_new && (
            <span className="px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide bg-[#C5A059] text-white">New</span>
          )}
        </div>
        {property.featured && (
          <span className="absolute top-3 right-12 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide bg-white/90 text-[#111827] backdrop-blur">
            Featured
          </span>
        )}
        <button
          onClick={(e) => { e.preventDefault(); toggleSave(property); }}
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/90 backdrop-blur flex items-center justify-center hover:scale-110 transition-transform"
          data-testid={`save-btn-${property.id}`}
          aria-label="Save property"
        >
          <Heart className={`w-4 h-4 ${saved ? "fill-[#C81D35] text-[#C81D35]" : "text-[#111827]"}`} />
        </button>
      </Link>

      <div className="p-5 flex flex-col flex-1">
        <p className="text-xl font-extrabold tracking-tight text-[#C81D35] font-body">{priceLabel(property) || "Price on application"}</p>
        <Link to={`/property/${property.slug}`}>
          <h3 className="mt-1.5 font-body font-bold text-[#111827] leading-snug line-clamp-1 hover:text-[#C81D35] transition-colors">
            {property.title}
          </h3>
        </Link>
        {location && (
          <p className="mt-1 text-sm text-muted-foreground flex items-center gap-1">
            <MapPin className="w-3.5 h-3.5" /> {location}
          </p>
        )}
        {property.summary && <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{property.summary}</p>}

        <div className="mt-4 pt-4 border-t border-border flex items-center gap-4 text-sm text-[#111827]/80">
          {property.bedrooms != null && (
            <span className="flex items-center gap-1.5"><BedDouble className="w-4 h-4 text-muted-foreground" /> {property.bedrooms}</span>
          )}
          {property.bathrooms != null && (
            <span className="flex items-center gap-1.5"><Bath className="w-4 h-4 text-muted-foreground" /> {property.bathrooms}</span>
          )}
          {property.size && (
            <span className="flex items-center gap-1.5"><Maximize className="w-4 h-4 text-muted-foreground" /> {property.size} {property.size_unit || "sq ft"}</span>
          )}
          {property.property_type && <span className="ml-auto text-xs font-medium text-muted-foreground">{property.property_type}</span>}
        </div>
      </div>
    </div>
  );
}
